<template>
	<transition name="notification-fade">
		<div v-show="visible" class="new-message-notification" :style="style" @mouseenter="clearTimer" @mouseleave="startTimer" @click="handleClick">
			<div class="new-message-header">
				{{ title }}
				<i class="notification-close el-icon-close" @click.stop="close"></i>
			</div>
			<div class="new-message-content flex items_center">
				<div class="new-message-avatar-group m-right-12">
					<el-image class="new-message-avatar" :src="avatar" fit="fill">
						<div slot="error" class="image-slot">
							<i class="el-icon-user"></i>
						</div>
					</el-image>
				</div>
				<div class="new-message-info">
					<div class="new-message-name m-bottom-6 ellipsis-1">{{ name }}</div>
					<div class="new-message-text ellipsis-1">{{ message }}</div>
				</div>
			</div>
			<div v-if="showProgress && duration > 0" class="notification-progress">
				<div class="progress-bar" :style="{ width: progressWidth + '%' }"></div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: 'MessageNotification',

	props: {
		title: {
			type: String,
			default: '新消息提醒'
		},
		name: {
			type: String,
			required: true
		},
		message: {
			type: String,
			required: true
		},
		avatar: {
			type: String,
			default: ''
		},
		duration: {
			type: Number,
			default: 5000
		},
		showProgress: {
			type: Boolean,
			default: true
		},
		offset: {
			type: Number,
			default: 20
		},
		onClick: {
			type: Function,
			default: null
		},
		onClose: {
			type: Function,
			default: null
		}
	},

	// 重要：data 中不要重复定义 props 中已有的属性
	data() {
		return {
			visible: false,
			timer: null,
			progressTimer: null,
			progressWidth: 100,
			currentOffset: 0
		};
	},

	computed: {
		style() {
			return {
				bottom: `${this.currentOffset}px`,
				right: `${this.offset}px`
			};
		}
	},

	mounted() {
		this.visible = true;
		this.startTimer();
	},

	beforeDestroy() {
		this.clearTimer();
	},

	methods: {
		startTimer() {
			if (this.duration > 0) {
				this.timer = setTimeout(() => {
					this.close();
				}, this.duration);

				if (this.showProgress) {
					const stepTime = this.duration / 100;
					this.progressTimer = setInterval(() => {
						if (this.progressWidth > 0) {
							this.progressWidth -= 1;
						} else {
							clearInterval(this.progressTimer);
						}
					}, stepTime);
				}
			}
		},

		clearTimer() {
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}
			if (this.progressTimer) {
				clearInterval(this.progressTimer);
				this.progressTimer = null;
			}
		},

		close() {
			this.clearTimer();
			this.visible = false;

			setTimeout(() => {
				this.$emit('close');
				if (this.onClose && typeof this.onClose === 'function') {
					this.onClose();
				}
				this.$destroy();
				if (this.$el && this.$el.parentNode) {
					this.$el.parentNode.removeChild(this.$el);
				}
			}, 300);
		},

		handleClick() {
			if (this.onClick && typeof this.onClick === 'function') {
				this.onClick();
			}
			this.$emit('click');
		}
	}
};
</script>

<style scoped lang="scss">
.new-message-notification {
	width: 294px;
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 99;
	cursor: pointer;
	box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
	transition: all 0.3s;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
	}

	.new-message-header {
		color: rgba(255, 255, 255, 1);
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		border-radius: 10px 10px 0px 0px;
		background: var(--el-kefu-color-primary1);
		padding: 9px 15px;
		position: relative;
		padding-right: 35px;

		.notification-close {
			position: absolute;
			right: 15px;
			top: 50%;
			transform: translateY(-50%);
			font-size: 12px;
			cursor: pointer;
			opacity: 0.8;
			transition: opacity 0.2s;

			&:hover {
				opacity: 1;
			}
		}
	}

	.new-message-content {
		padding: 13px 17px;
		border-radius: 0px 0px 10px 10px;
		background: rgba(255, 255, 255, 1);

		.new-message-avatar-group {
			width: 48px;
			height: 48px;
			background: rgb(217, 214, 214);
			border-radius: 50%;
			overflow: hidden;
			flex-shrink: 0;

			.new-message-avatar {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}

			.image-slot {
				width: 100%;
				height: 100%;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 24px;
				color: #909399;
			}
		}

		.new-message-info {
			flex: 1;
			min-width: 0;

			.new-message-name {
				color: rgba(68, 76, 105, 1);
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
			}

			.new-message-text {
				color: rgba(165, 165, 165, 1);
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
			}
		}
	}

	.notification-progress {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 4px;
		background-color: rgba(0, 0, 0, 0.05);
		border-radius: 0 0 10px 10px;
		overflow: hidden;

		.progress-bar {
			height: 100%;
			background-color: rgba(24, 188, 156, 0.6);
			transition: width 0.1s linear;
		}
	}
}

.notification-fade-enter-active,
.notification-fade-leave-active {
	transition: all 0.3s ease;
}

.notification-fade-enter {
	opacity: 0;
	transform: translateX(30px);
}

.notification-fade-leave-to {
	opacity: 0;
	transform: translateX(30px);
}

.flex {
	display: flex;
}

.items_center {
	align-items: center;
}

.m-right-12 {
	margin-right: 12px;
}

.m-bottom-6 {
	margin-bottom: 6px;
}

.ellipsis-1 {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
